// constants
import {
    DealCategory,
    DEFAULT_SUCCESS_DATA,
    DO_DINECASH_STORE_HELP_WEBVIEW_URL,
    DO_EVENT_RESERVATION_HELP_WEBVIEW_URL,
    DO_HELP_WEBVIEW_DEFAULT_URL,
    DO_STEALSTORE_RESERVATION_HELP_WEBVIEW_URL,
    USER_STATUS_SUPER,
} from "@dineout-payment-success-ui/constants";

// interfaces
import {
    IBill,
    IBillEntries,
    ICtx,
    IMetaData,
    IPaymentSuccessData,
    ISubscription,
    ISuccessBannerDetails,
    IConditionalProps,
    IStealDealPaymentSuccessData,
} from "@dineout-payment-success-ui/interfaces";

// internal-deps
import { IGroupedItems } from "@swiggy-private/checkout-types";
import { getNestedValue } from "@swiggy-private/js-utils";
import { isEmpty, safeJsonParse } from "@swiggy-private/core-ui";

// helpers/utils
import {
    currencyFormat,
    getAllBillEntries,
    getPaymentSuccessOneLogoStyle,
} from "@dineout-payment-success-ui/utils/Utils";
import { getBookingDetails, getTicketAttributes } from "./BookingSuccessHelpers";
import {
    getFeeObject,
    getFormattedDiscounts,
    getRestaurantInfo,
    getTipSubText,
    getTipTitle,
    getTipValue,
    getTotalAmountWithoutTip,
} from "./BillHelper";

export const getDOPaymentSuccessData = (metaData: string, isTableReservation?: boolean): IPaymentSuccessData => {
    const metaDataJson = safeJsonParse<IMetaData>(metaData);
    if (isEmpty(metaDataJson)) {
        return DEFAULT_SUCCESS_DATA;
    }
    const { storeInfo, bill, subscriptionDetails, ctx, dealCategory, items, savingCards, splitPayWidget, tipWidget } =
        metaDataJson || {};
    const restaurantInfo = getRestaurantInfo(storeInfo);
    const successBannerInfo = getSuccessBannerDetails(bill, subscriptionDetails, ctx?.background_image);
    const oneBenefitsCardInfo = getOneBenefitsCardDetails(bill?.totalSavings, metaDataJson?.superData?.userStatus, ctx);
    const billDetailsInfo = getBillDetails(
        bill,
        subscriptionDetails,
        isTableReservation,
        items?.[0]?.guestCount,
        dealCategory,
    );
    const bookingDetails = getBookingDetails(items?.[0]);
    const ticketAttributes = getTicketAttributes(bill, ctx, items?.[0]);
    const paidImage = ctx?.paid_image;
    const styles = ctx?.styles;
    const restaurantId = `${storeInfo?.id}`;
    const itemsData = items;
    const cashbackInfoText = metaDataJson?.helperTexts?.cashbackInfoText;
    return {
        restaurantInfo,
        successBannerInfo,
        oneBenefitsCardInfo,
        billDetailsInfo,
        paidImage,
        restaurantId,
        dealCategory,
        styles,
        bookingDetails,
        ticketAttributes,
        itemsData,
        cashbackInfoText,
        savingCards,
        splitPayWidget,
        tipWidget,
    };
};

export const getStealDealPaymentSuccessData = (metaData: string): IStealDealPaymentSuccessData => {
    const metaDataJson = safeJsonParse<IMetaData>(metaData);
    if (isEmpty(metaDataJson)) {
        return DEFAULT_SUCCESS_DATA;
    }
    const { bill, dealCategory, groupedItems, brandInfo, subscriptionDetails, items, aggregatedItemsInfo } =
        metaDataJson || {};
    const billDetailsInfo = getBillDetails(bill, subscriptionDetails, false, items?.[0]?.guestCount, dealCategory);

    const transformedGroupedItems = (groupedItems || []).map((brand: IGroupedItems) => {
        const updatedItems = (brand.items || []).map((item: IGroupedItems["items"][number]) => {
            return {
                ...item,
                itemId: item?.itemId,
                itemName: item?.name || "",
                itemPrice: item?.price,
                quantity: item?.quantity || 0,
                min: 0,
                max: 0,
            };
        });

        return {
            ...brand,
            items: updatedItems,
        };
    });
    return {
        groupedItems: transformedGroupedItems,
        brandInfo,
        billDetailsInfo,
        aggregatedItemsInfo,
    };
};

const getOneBenefitsCardDetails = (totalSavings: number, userStatus: string, successData: ICtx) => {
    const oneLogo = successData?.membership_white_image;
    const savingsValue = totalSavings && currencyFormat(Math.floor(Number(totalSavings)));
    const newSavingsValue = String(savingsValue).replace(/,/g, "");
    const numericValue = parseInt(newSavingsValue);
    const isSuperUser = userStatus === USER_STATUS_SUPER;
    const oneLogoUrl = oneLogo || "";
    const imageUrl = getNestedValue(successData, "post_order_screen_redeem_message.imageUrl", "");
    const backgroundColor = getNestedValue(successData, "post_order_screen_redeem_message.backgroundColor", "");
    const borderColor = getNestedValue(successData, "post_order_screen_redeem_message.borderColor", "");

    const calloutInfo = {
        imageUrl: imageUrl,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
    };
    const successAnimationData = {
        bottomText1: successData?.post_order_screen_message_text_line1 || "",
        bottomText2: successData?.post_order_screen_message_text_line2 || "",
        bottomText3: successData?.post_order_screen_message_text_line3 || "",
    };
    return {
        savingsValue,
        userStatus: isSuperUser,
        oneLogoStyle: getPaymentSuccessOneLogoStyle(),
        oneLogoUrl,
        successAnimationData,
        numericValue,
        calloutInfo,
    };
};

const getSuccessBannerDetails = (
    billDetails: IBill,
    subscription: ISubscription,
    backgroundImage: string,
): ISuccessBannerDetails => {
    const { charges, billAmountWithoutDiscounts, enteredBillAmount } = billDetails;
    const tipString = getTipValue(charges);
    const tip = parseInt(tipString);
    const totalBillWithoutTip = getTotalAmountWithoutTip(tipString, billAmountWithoutDiscounts);
    const billAmount = currencyFormat(totalBillWithoutTip || 0);
    const paidAmount = currencyFormat(Math.floor(billDetails?.totalBill) || 0);
    const subscriptionValue = parseInt(subscription?.finalPrice || "0");
    const totalAmount = enteredBillAmount > 0 ? currencyFormat(enteredBillAmount || 0) : billAmount; // eneteredBillAmount right now we are considering for the rush hour scenerios,
    return {
        totalAmount,
        paidAmount,
        tip,
        subscriptionValue,
        backgroundImage,
    };
};

export const getBillDetails = (
    billDetails: IBill,
    subscriptionDetails: ISubscription,
    isTableReservation = false,
    guestCount = 0,
    dealCategory?: string,
    // eslint-disable-next-line max-params
): IBillEntries[] => {
    const {
        discounts,
        charges,
        billAmountWithoutDiscounts,
        totalBillAmount,
        advanceAmount,
        totalRewardsRedeemed,
        enteredBillAmount,
        billedItems,
        taxes,
        partialPaymentDetails,
    } = billDetails;
    const isEventReservation = dealCategory === DealCategory.EVENTS;
    const formattedDiscounts = getFormattedDiscounts(discounts);
    const feeObject = getFeeObject(charges);
    const tip = getTipValue(charges);
    const tipTitle = getTipTitle(charges);
    const tipSubText = getTipSubText(charges);
    const totalBillWithoutTip = getTotalAmountWithoutTip(tip, billAmountWithoutDiscounts);
    const redeemable = billedItems?.[0]?.item?.ticketAttributes?.redeemable;
    const billItems = isEventReservation // this condition is to avoid duplicate bill items for table reservation
        ? billedItems?.map((item) => {
              const { savingsLine, itemId, ticketAttributes } = item.item;
              const { totalEffectivePrice, totalBasePrice } = ticketAttributes;
              return {
                  id: itemId,
                  leftText: savingsLine || "",
                  rightText: totalEffectivePrice.toString(),
                  strikethroughAmount: totalBasePrice - totalEffectivePrice ? totalBasePrice.toString() : "",
              };
          })
        : [];
    const billEntries = getAllBillEntries({
        discountList: formattedDiscounts,
        subscription: subscriptionDetails,
        feeObject,
        totalBillAmount: totalBillWithoutTip,
        toPayAmount: Math.floor(totalBillAmount),
        tip,
        tipTitle,
        tipSubText,
        advanceAmount: Math.floor(advanceAmount),
        enteredBillAmount: Math.floor(enteredBillAmount),
        isTableReservation: isTableReservation,
        guestCount,
        isCart: false, //passing false since this function only called on success page
        redeemable,
        billItems,
        dealCategory,
        dineCash: totalRewardsRedeemed,
        gst: taxes?.[0]?.totalGST,
        partialPaymentDetails,
    });
    return billEntries;
};

export const getHelpWebviewUrl = (
    isEventReservation?: boolean,
    isStealStoreFlow?: boolean,
    couponPurchase?: boolean,
): string => {
    if (isEventReservation) {
        return DO_EVENT_RESERVATION_HELP_WEBVIEW_URL;
    }
    if (isStealStoreFlow) {
        return DO_STEALSTORE_RESERVATION_HELP_WEBVIEW_URL;
    }

    if (couponPurchase) {
        return DO_DINECASH_STORE_HELP_WEBVIEW_URL;
    }

    if (typeof window !== "undefined") {
        return window?.__DO_HELP_WEB_VIEW_URL__ || DO_HELP_WEBVIEW_DEFAULT_URL;
    }
    return DO_HELP_WEBVIEW_DEFAULT_URL;
};

export const getBillEntriesList = (billEntries: IBillEntries[]) =>
    billEntries.reduce(
        (details, billItem) => {
            if (billItem.leftElement?.text && billItem.rightElement?.text) {
                details[billItem.leftElement.text] = billItem.rightElement.text;
            }
            return details;
        },
        {} as Record<string, string>,
    );

export const getConditionalProps = (
    rewardsWidget: IConditionalProps["earnedDetails"] | undefined,
    ctaSubTextMeta: IConditionalProps["ctaSubTextMeta"] | undefined,
    whatToDoNextSrc: string | undefined,
): IConditionalProps => {
    if (rewardsWidget) {
        return {
            earnedDetails: rewardsWidget,
        };
    }
    return {
        ctaSubTextMeta,
        footerImageSrc: whatToDoNextSrc,
        earnedDetails: rewardsWidget,
    };
};
